import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Col,
  Form,
  type FormInstance,
  InputNumber,
  Row,
  Slider,
  Space,
  Tooltip,
  Typography,
} from "antd";
import FormItem from "antd/es/form/FormItem";
import Checkbox, { type CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";
import { useCallback, useEffect, useMemo } from "react";
import type { AffineTransformation, APIDataLayer } from "types/api_types";
import {
  AXIS_TO_TRANSFORM_INDEX,
  EXPECTED_SETTINGS_TRANSFORMATION_LENGTH,
  fromCenterToOriginAsAffine,
  fromOriginToCenterAsAffine,
  getRotationMatrixAroundAxis,
  IDENTITY_TRANSFORM,
  type RotationAndMirroringSettings,
  settingsTransformationEqualsAffineIdentityTransform,
} from "viewer/model/accessors/dataset_layer_transformation_accessor";
import BoundingBox from "viewer/model/bucket_data_handling/bounding_box";
import { FormItemWithInfo } from "./helper_components";

const { Text } = Typography;

type AxisRotationFormItemProps = {
  form: FormInstance | undefined;
  axis: "x" | "y" | "z";
};

export function getDatasetBoundingBoxFromLayers(
  layers: APIDataLayer[] | undefined,
): BoundingBox | undefined {
  if (layers == null || layers.length === 0) {
    return undefined;
  }
  let datasetBoundingBox = BoundingBox.fromBoundBoxObject(layers[0].boundingBox);
  for (let i = 1; i < layers.length; i++) {
    datasetBoundingBox = datasetBoundingBox.extend(
      BoundingBox.fromBoundBoxObject(layers[i].boundingBox),
    );
  }
  return datasetBoundingBox;
}

const AxisRotationFormItem: React.FC<AxisRotationFormItemProps> = ({
  form,
  axis,
}: AxisRotationFormItemProps) => {
  const dataLayers: APIDataLayer[] = Form.useWatch(["dataSource", "dataLayers"], {
    form,
    preserve: true,
  });
  const datasetBoundingBox = useMemo(
    () => getDatasetBoundingBoxFromLayers(dataLayers),
    [dataLayers],
  );
  // Update the transformations in case the user changes the dataset bounding box.
  useEffect(() => {
    if (
      datasetBoundingBox == null ||
      dataLayers[0].coordinateTransformations?.length !== EXPECTED_SETTINGS_TRANSFORMATION_LENGTH ||
      !form
    ) {
      return;
    }
    const rotationValues: {
      x: RotationAndMirroringSettings;
      y: RotationAndMirroringSettings;
      z: RotationAndMirroringSettings;
    } = form.getFieldValue(["datasetRotation"]);
    const transformations = getRotationalTransformation(datasetBoundingBox, rotationValues);
    const dataLayersWithUpdatedTransforms = dataLayers.map((layer) => {
      return {
        ...layer,
        coordinateTransformations: transformations,
      };
    });
    form.setFieldValue(["dataSource", "dataLayers"], dataLayersWithUpdatedTransforms);
  }, [datasetBoundingBox, dataLayers, form]);

  const setMatrixRotationsForAllLayer = useCallback(
    (rotationInDegrees: number | undefined, isMirrored?: boolean): void => {
      if (!form) {
        return;
      }
      const dataLayers: APIDataLayer[] = form.getFieldValue(["dataSource", "dataLayers"]);
      const datasetBoundingBox = getDatasetBoundingBoxFromLayers(dataLayers);
      if (datasetBoundingBox == null) {
        return;
      }
      const rotationValues: RotationAndMirroringSettings = {
        ...form.getFieldValue(["datasetRotation"])[axis],
      };
      if (rotationInDegrees !== undefined) {
        rotationValues.rotationInDegrees = rotationInDegrees;
      }
      if (isMirrored !== undefined) {
        rotationValues.isMirrored = isMirrored;
      }
      const rotationMatrix = getRotationMatrixAroundAxis(axis, rotationValues);
      const dataLayersWithUpdatedTransforms: APIDataLayer[] = dataLayers.map((layer) => {
        let transformations = layer.coordinateTransformations;
        if (
          transformations == null ||
          transformations.length !== EXPECTED_SETTINGS_TRANSFORMATION_LENGTH
        ) {
          transformations = [
            fromCenterToOriginAsAffine(datasetBoundingBox),
            IDENTITY_TRANSFORM,
            IDENTITY_TRANSFORM,
            IDENTITY_TRANSFORM,
            fromOriginToCenterAsAffine(datasetBoundingBox),
          ];
        }
        transformations[AXIS_TO_TRANSFORM_INDEX[axis]] = rotationMatrix;
        const updatedTransformations = settingsTransformationEqualsAffineIdentityTransform(
          transformations,
        )
          ? null
          : transformations;

        return {
          ...layer,
          coordinateTransformations: updatedTransformations,
        };
      });
      form.setFieldValue(["dataSource", "dataLayers"], dataLayersWithUpdatedTransforms);
    },
    [axis, form],
  );
  return (
    <Row gutter={24}>
      <Col span={8}>
        <FormItemWithInfo
          name={["datasetRotation", axis, "rotationInDegrees"]}
          label={`${axis.toUpperCase()} Axis Rotation`}
          info={`Change the dataset's rotation in 90 degree steps around the ${axis}-axis.`}
          colon={false}
        >
          <Slider min={0} max={270} step={90} onChange={setMatrixRotationsForAllLayer} />
        </FormItemWithInfo>
      </Col>
      <Col span={4} style={{ marginRight: -12 }}>
        <FormItem
          name={["datasetRotation", axis, "rotationInDegrees"]}
          colon={false}
          label=" " /* Whitespace label is needed for correct formatting*/
        >
          <InputNumber readOnly variant="borderless" />
        </FormItem>
      </Col>
      <Col span={4} style={{ marginRight: -12 }}>
        <FormItem
          name={["datasetRotation", axis, "isMirrored"]}
          colon={false}
          valuePropName="checked"
          label={`Mirror ${axis.toUpperCase()} Axis`} /* Whitespace label is needed for correct formatting*/
        >
          <Checkbox
            onChange={(evt: CheckboxChangeEvent) =>
              setMatrixRotationsForAllLayer(undefined, evt.target.checked)
            }
          />
        </FormItem>
      </Col>
    </Row>
  );
};

type AxisRotationSettingForDatasetProps = {
  form: FormInstance | undefined;
};

export type DatasetRotationAndMirroringSettings = {
  x: RotationAndMirroringSettings;
  y: RotationAndMirroringSettings;
  z: RotationAndMirroringSettings;
};

export const AxisRotationSettingForDataset: React.FC<AxisRotationSettingForDatasetProps> = ({
  form,
}: AxisRotationSettingForDatasetProps) => {
  const isRotationOnly: boolean = form?.getFieldValue(["isRotationOnly"]) || false;
  if (!isRotationOnly) {
    return (
      <Tooltip
        title={
          <div>
            Each layers transformations must be equal and each layer needs exactly 5 affine
            transformation with the following schema:
            <ul>
              <li>Translation to the origin</li>
              <li>Rotation around the x-axis</li>
              <li>Rotation around the y-axis</li>
              <li>Rotation around the z-axis</li>
              <li>Translation back to the original position</li>
            </ul>
            To easily enable this setting, delete all coordinateTransformations of all layers by
            choosing "Transformation Mode: None" in the dropdown above, save and reload the dataset
            settings.
          </div>
        }
      >
        <Space orientation="vertical" size="small">
          <Text type="secondary">
            Setting a dataset's rotation is only supported when all layers have the same rotation
            transformation. <InfoCircleOutlined />
          </Text>
          <Text type="secondary">
            To enable this setting, choose "Transformation Mode: None" in the dropdown above, save
            and reload the dataset settings.
          </Text>
        </Space>
      </Tooltip>
    );
  }

  return (
    <div>
      <AxisRotationFormItem form={form} axis="x" />
      <AxisRotationFormItem form={form} axis="y" />
      <AxisRotationFormItem form={form} axis="z" />
    </div>
  );
};

export function getRotationalTransformation(
  datasetBoundingBox: BoundingBox,
  rotationValues: {
    x: RotationAndMirroringSettings;
    y: RotationAndMirroringSettings;
    z: RotationAndMirroringSettings;
  },
): AffineTransformation[] {
  return [
    fromCenterToOriginAsAffine(datasetBoundingBox),
    getRotationMatrixAroundAxis("x", rotationValues["x"]),
    getRotationMatrixAroundAxis("y", rotationValues["y"]),
    getRotationMatrixAroundAxis("z", rotationValues["z"]),
    fromOriginToCenterAsAffine(datasetBoundingBox),
  ];
}
